'use client';

import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import { useRef, useState } from 'react';
import { CountryMap } from './CountryMap';
import type { LocationMapResolved } from './types';
import { Icon } from '@/components/Icon';
import { Link } from '@/components/Link';
import { useRouter } from '@/i18n/navigation';
import { countryFor, regionHref } from '@/lib/countries';
import { pushCmsEventTracking } from '@/lib/gtm/pushCmsEventTracking';
import { useRuntimeConfig } from '@/lib/runtime-config/RuntimeConfigProvider';

interface LocationMapComponentProps {
  resolved: LocationMapResolved;
}

export const LocationMapComponent = ({
  resolved,
}: LocationMapComponentProps) => {
  const { brand } = useRuntimeConfig();
  const country = countryFor(brand);
  const router = useRouter();
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Spec 10.03.03.14 AC #5: countries without a map render nothing at all —
  // no container, no spacing. Placed after the hooks so the hook count stays
  // constant, and before any markup so no whitespace is left behind.
  if (!country) {
    return null;
  }

  const trackStateSelect = () => {
    if (resolved.tracking?.length) {
      pushCmsEventTracking(resolved.tracking);
    }
  };

  // The state list uses real anchors so crawlers can follow them. The map's
  // regions are SVG paths rather than links, so map clicks navigate
  // imperatively — via the locale-aware router, so both routes agree.
  const handleStateSelect = (isoId: string) => {
    const state = country.regions.find((s) => s.id === isoId);
    if (state) {
      trackStateSelect();
      router.push(regionHref(state));
    }
  };

  return (
    <div className="flex flex-col gap-8 pt-4 md:flex-row md:items-start">
      <div className="w-full md:w-1/2">
        <CountryMap
          country={country.mapKey}
          hoveredId={hoveredId}
          onHover={setHoveredId}
          onSelect={handleStateSelect}
        />
      </div>

      {/* Mobile: dropdown select */}
      <div
        ref={dropdownRef}
        className="border-card-border overflow-hidden rounded-2xl border md:hidden"
      >
        <button
          type="button"
          onClick={() => setDropdownOpen((o) => !o)}
          className="bg-card-bg flex w-full items-center justify-between px-4 py-3 text-base"
        >
          <span className="text-foreground/50">{country.name}</span>
          <Icon
            src="/icons/svg/chevron-down.svg"
            size={20}
            className={classNames(
              'shrink-0 transition-transform',
              dropdownOpen && 'rotate-180',
            )}
          />
        </button>

        {dropdownOpen && (
          <div className="divide-card-border border-card-border divide-y border-t bg-white">
            {country.regions.map((state) => (
              <Link
                key={state.id}
                href={regionHref(state)}
                onClick={() => {
                  setDropdownOpen(false);
                  trackStateSelect();
                }}
                className="text-foreground block w-full px-4 py-3 text-left text-base"
              >
                {state.name}
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Desktop: 2-column list, filled top-to-bottom per column */}
      <div className="hidden flex-1 md:flex md:gap-x-6">
        {[country.regions.slice(0, 9), country.regions.slice(9)].map(
          (column, colIdx) => (
            <div key={colIdx} className="border-card-border flex-1 border-t">
              {column.map((state) => (
                <Link
                  key={state.id}
                  href={regionHref(state)}
                  onMouseEnter={() => setHoveredId(state.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  onClick={trackStateSelect}
                  className={classNames(
                    'border-card-border flex w-full cursor-pointer items-center gap-2 border-b py-3 text-left text-base transition-colors',
                    hoveredId === state.id
                      ? 'text-primary'
                      : 'text-foreground hover:text-primary',
                  )}
                >
                  <Icon
                    src="/icons/svg/arrow-circle-right-filled.svg"
                    size={20}
                    className="shrink-0"
                  />
                  {state.name}
                </Link>
              ))}
            </div>
          ),
        )}
      </div>
    </div>
  );
};
